import React from 'react';
import { PROPOSAL_BUNDLE_CATEGORY, isHotelProposalBundle, type ProposalBundle } from '../../types/proposalBundles';
import type {
  OnProposalBundleClose,
  OnProposalBundleFeedbackUpdate,
  OnProposalItemSelect
} from '../../types/proposalHandlers';
import type { ChatMessage } from '../../types/messages';
import { ForeignFlightProposalSection } from './ForeignFlightProposalSection';
import ProposalBundleCards from './ProposalBundleCards';
import ProposalCardsContainer from './shared/ProposalCardsContainer';
import ProposalMessageRow from './shared/ProposalMessageRow';
import StandardProposalInstruction from './shared/StandardProposalInstruction';

const isForeignRoundTripLegBundle = (bundle: ProposalBundle): boolean =>
  (bundle.category === PROPOSAL_BUNDLE_CATEGORY.OUTWARD ||
    bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOMEWARD) &&
  Boolean(bundle.hasForeignFlightRoundTrip);

const hasForeignRoundTripPair = (bundles: ProposalBundle[]): boolean => {
  const outward = bundles.some(
    bundle => bundle.category === PROPOSAL_BUNDLE_CATEGORY.OUTWARD && bundle.hasForeignFlightRoundTrip
  );
  const homeward = bundles.some(
    bundle => bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOMEWARD && bundle.hasForeignFlightRoundTrip
  );
  return outward && homeward;
};

const isForeignRoundTripLegMessage = (bundles: ProposalBundle[]): boolean => {
  if (bundles.length === 0) {
    return false;
  }
  if (hasForeignRoundTripPair(bundles)) {
    return true;
  }
  if (bundles.some(bundle => bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOTEL)) {
    return false;
  }
  return bundles.some(bundle => isForeignRoundTripLegBundle(bundle));
};

const hasPartitionedHotelBundle = (bundles: ProposalBundle[]): boolean =>
  bundles.some(
    bundle =>
      isHotelProposalBundle(bundle) &&
      bundle.partition.partitioned === true &&
      (bundle.partition.segmentCount ?? 0) > 1
  );

interface Props {
  message: ChatMessage;
  bundles: ProposalBundle[];
  tripId: number;
  includeTax: boolean;
  disabled?: boolean;
  onSelectItem?: OnProposalItemSelect;
  onCloseItem?: OnProposalBundleClose;
  onBundleFeedbackUpdate?: OnProposalBundleFeedbackUpdate;
  isSpLayout?: boolean;
}

const ProposalSection = ({
  message,
  bundles,
  tripId,
  includeTax,
  disabled = false,
  onSelectItem,
  onCloseItem,
  onBundleFeedbackUpdate,
  isSpLayout = false
}: Props) => {
  if (isForeignRoundTripLegMessage(bundles)) {
    return (
      <ForeignFlightProposalSection
        message={message}
        bundles={bundles}
        tripId={tripId}
        includeTax={includeTax}
        disabled={disabled}
        onSelectItem={onSelectItem}
        onCloseItem={onCloseItem}
        onBundleFeedbackUpdate={onBundleFeedbackUpdate}
        isSpLayout={isSpLayout}
      />
    );
  }

  return (
    <ProposalMessageRow
      isNew={message.isNew}
      createdAt={message.createdAt}
      footer={
        bundles.length === 0 ? undefined : (
          <StandardProposalInstruction partitionedHotel={hasPartitionedHotelBundle(bundles)} />
        )
      }
    >
      <ProposalCardsContainer introMessage={message.text}>
        <ProposalBundleCards
          bundles={bundles}
          includeTax={includeTax}
          onSelect={onSelectItem}
          onClose={onCloseItem}
          onFeedbackUpdate={onBundleFeedbackUpdate}
          disabled={disabled}
          tripId={tripId}
          isSpLayout={isSpLayout}
        />
      </ProposalCardsContainer>
    </ProposalMessageRow>
  );
};

export default ProposalSection;
